import { Dispatch, FC, SetStateAction, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { profile_preview } from "@/constants";
import Modal from "..";
import { ContainerHeading, StyledContainedButton } from "./styles";
import { Close as CloseIcon } from "@mui/icons-material";
import PasswordStrengthBar from "@/components/password-strength-bar";
import { useChangePasswordMutation } from "@/services";
import { useSnackbar } from "@/hooks";

interface IChangePassword {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

type ChangePasswordFormType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const ChangePasswordModal: FC<IChangePassword> = ({ open, setOpen }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ChangePasswordFormType>({
    defaultValues: {
      confirmNewPassword: "",
      currentPassword: "",
      newPassword: "",
    },
  });

  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const { show } = useSnackbar();

  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const onSubmit = async (data: ChangePasswordFormType) => {
    try {
      await changePassword({
        previousPassword: data.currentPassword,
        newPassword: data.newPassword,
      }).unwrap();
      setOpen(false);
      reset();
      show("Password changed successfully", "", "success");
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  const handleTriggerPasswordShow = (name: keyof ChangePasswordFormType) => {
    setShowPassword((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  // Password requirements
  const newPassword = watch("newPassword", "");
  const passwordRequirements = [
    {
      label: "Be minimum eight characters",
      condition: newPassword.length >= 8,
    },
    { label: "Have at least one number", condition: /\d/.test(newPassword) },
    {
      label: "Have at least one special character",
      condition: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    },
  ];

  const passwordStrength =
    passwordRequirements.filter((req) => req.condition).length *
    (100 / passwordRequirements.length);

  const handleModalClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <Modal open={open} onClose={() => handleModalClose()} maxWidth="sm" hideClose>
      <>
        <ContainerHeading>
          <Stack justifyContent={"flex-end"} alignItems={"flex-end"}>
            <IconButton sx={{ padding: 0 }} onClick={handleModalClose}>
              <CloseIcon sx={{ position: "absolute", top: "-9px" }} />
            </IconButton>
          </Stack>
          <Box display="flex" alignSelf="center">
            <img
              src={profile_preview}
              alt="profile-preview"
              style={{ marginRight: "10px" }}
            />

            <Box>
              <Typography variant="h3" fontWeight={600} color={"#030712"}>
                Change Password
              </Typography>

              <Typography variant="h6" fontWeight={600} color={"#8D9299"}>
                Change your password every 3 months to enhance your account
                security.
              </Typography>
            </Box>
          </Box>
        </ContainerHeading>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2} my={2}>
            {/* Current Password Field */}
            <Controller
              name="currentPassword"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Current Password is required.",
                },
              }}
              render={({ field }) => (
                <>
                  <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                    Current Password
                  </Typography>
                  <TextField
                    {...field}
                    type={showPassword.currentPassword ? "text" : "password"}
                    placeholder="Current Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTriggerPasswordShow("currentPassword")
                            }
                          >
                            {showPassword.currentPassword ? (
                              <FiEyeOff size={20} color="#AEAFB3" />
                            ) : (
                              <FiEye size={20} color="#AEAFB3" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors.currentPassword}
                    helperText={
                      errors.currentPassword
                        ? errors.currentPassword.message
                        : ""
                    }
                  />
                </>
              )}
            />

            {/* New Password Field */}
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "New Password is required.",
                },
                validate: {
                  minLength: (value) =>
                    value.length >= 8 ||
                    "Password must be at least 8 characters.",
                  hasNumber: (value) =>
                    /\d/.test(value) ||
                    "Password must contain at least one number.",
                  hasSpecialChar: (value) =>
                    /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                    "Password must contain at least one special character.",
                },
              }}
              render={({ field }) => (
                <>
                  <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                    New Password
                  </Typography>
                  <TextField
                    {...field}
                    type={showPassword.newPassword ? "text" : "password"}
                    placeholder="New Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTriggerPasswordShow("newPassword")
                            }
                          >
                            {showPassword.newPassword ? (
                              <FiEyeOff size={20} color="#AEAFB3" />
                            ) : (
                              <FiEye size={20} color="#AEAFB3" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors.newPassword}
                    helperText={
                      errors.newPassword ? errors.newPassword.message : ""
                    }
                  />
                  {/* Password Strength Indicator */}
                  <PasswordStrengthBar strength={passwordStrength} />
                  <Typography variant="body2">Strong</Typography>

                  {/* Password Requirements */}
                  <List dense>
                    {passwordRequirements.map((req, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {req.condition ? (
                            <CheckCircle color="success" />
                          ) : (
                            <RadioButtonUnchecked color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={req.label} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            />

            {/* Confirm New Password Field */}
            <Controller
              name="confirmNewPassword"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Confirm Password is required.",
                },
                validate: (value: string) => {
                  if (value) {
                    if (value !== newPassword) {
                      return "Passwords don't match";
                    }
                  }
                },
              }}
              render={({ field }) => (
                <>
                  <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                    Confirm New Password
                  </Typography>
                  <TextField
                    {...field}
                    type={showPassword.confirmNewPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTriggerPasswordShow("confirmNewPassword")
                            }
                          >
                            {showPassword.confirmNewPassword ? (
                              <FiEyeOff size={20} color="#AEAFB3" />
                            ) : (
                              <FiEye size={20} color="#AEAFB3" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors.confirmNewPassword}
                    helperText={
                      errors.confirmNewPassword
                        ? errors.confirmNewPassword.message
                        : ""
                    }
                  />
                </>
              )}
            />
          </Stack>

          {/* Modal Actions */}
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <StyledContainedButton
              variant="outlined"
              onClick={handleModalClose}
              title="Close"
            />
            <StyledContainedButton
              size="large"
              type="submit"
              title={"Change Password"}
              {...(isLoading && {
                startIcon: (
                  <CircularProgress sx={{ color: "white" }} size={20} />
                ),
              })}
            />
          </Stack>
        </form>
      </>
    </Modal>
  );
};

export default ChangePasswordModal;
